import {
  fetchTeacherLessonGroups,
  fetchTeacherMentorClasses,
} from "classroom/api/teacher";
import {
  LESSON_CLASS_ROUTE_NAMES,
  MENTOR_CLASS_ROUTE_NAMES,
} from "classroom/routes/routeNames";
import { AGGREGATE_MENTOR_CLASS } from "classroom/constants";
import type {
  ClassroomLessonClassListItem,
  ClassroomMenuChild,
  ClassroomMenuGroup,
  ClassroomMenuPlaceholder,
} from "classroom/types";
import { mentorClassIcon, toClassicIcon, usersIcon } from "common/defaultIcons";
import { userHasRole } from "composables/useHelpers";
import { useAuthStore } from "core/auth/auth.store";
import { acceptHMRUpdate, defineStore } from "pinia";
import { TEACHER_ROLES } from "shared/constants";
import type { HasFetchedState } from "shared/types";
import { resetHasFetched, shouldFetch } from "shared/utils";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

type FetchingMap = {
  classes: false | Promise<void>;
};

export const useClassroomNavigationStore = defineStore(
  "classroomNavigation",
  () => {
    const router = useRouter();

    const mentorClasses = ref<string[]>([]);
    const lessonClasses = ref<ClassroomLessonClassListItem[]>([]);

    const isFetching = ref<FetchingMap>({
      classes: false,
    });

    const hasFetched = ref<HasFetchedState>({
      classes: false,
    });

    const canSeeClassroomMenu = computed<boolean>(() =>
      userHasRole(TEACHER_ROLES),
    );

    const menuGroups = computed<ClassroomMenuGroup[]>(() => {
      const groups: ClassroomMenuGroup[] = [];
      const fetched = hasFetched.value.classes !== false;

      // Once fetched, an empty group shows a non-navigable placeholder label
      // instead of nothing. Before the fetch resolves we render no children so
      // the placeholder doesn't flash during loading.
      const withPlaceholder = (
        children: ClassroomMenuChild[],
        groupKey: string,
        emptyTitle: string,
      ): (ClassroomMenuChild | ClassroomMenuPlaceholder)[] =>
        fetched && children.length === 0
          ? [
              {
                key: `${groupKey}-empty`,
                meta: { title: emptyTitle },
                placeholder: true,
              },
            ]
          : children;

      // Only surface a group when its target route is registered — otherwise
      // the child router-links resolve to a non-existent route and vue-router
      // throws. Lesson group routes aren't registered yet, so that group stays
      // hidden until they are.
      if (router.hasRoute(MENTOR_CLASS_ROUTE_NAMES.MAIN)) {
        const mentorChildren: ClassroomMenuChild[] = mentorClasses.value.map(
          (className) => ({
            key: `mentor-${className}`,
            name: MENTOR_CLASS_ROUTE_NAMES.MAIN,
            to: {
              name: MENTOR_CLASS_ROUTE_NAMES.MAIN,
              params: { mentorClass: className },
            },
            meta: { title: className },
          }),
        );

        // With more than one mentor class, surface an aggregate item that
        // combines every mentor student into one virtual class.
        if (mentorClasses.value.length > 1) {
          mentorChildren.unshift({
            key: "mentor-all",
            name: MENTOR_CLASS_ROUTE_NAMES.MAIN,
            to: {
              name: MENTOR_CLASS_ROUTE_NAMES.MAIN,
              params: { mentorClass: AGGREGATE_MENTOR_CLASS },
            },
            meta: { title: "Alle mentorleerlingen" },
          });
        }

        const singleMentorClass = mentorClasses.value.length === 1;

        const mentorGroup: ClassroomMenuGroup = {
          key: "mentor",
          meta: {
            title: "Mijn mentorklas",
            icon: toClassicIcon(mentorClassIcon),
          },
          // A single class needs no submenu — the parent links straight to it —
          // so we render no children (hiding the chevron) and surface the class
          // as a badge instead.
          children: singleMentorClass
            ? []
            : withPlaceholder(mentorChildren, "mentor", "Geen mentorklas"),
        };

        if (singleMentorClass === true) {
          const className = mentorClasses.value[0];
          mentorGroup.name = MENTOR_CLASS_ROUTE_NAMES.MAIN;
          mentorGroup.to = {
            name: MENTOR_CLASS_ROUTE_NAMES.MAIN,
            params: { mentorClass: className },
          };
          mentorGroup.meta.badge = {
            label: className,
            // Empty color/text-color skip Quasar's palette classes (which are
            // !important and would win) so the Tailwind classes can paint the
            // pill to match the design: a pale green fill with navy text.
            color: "",
            textColor: "",
            class: "!tw-bg-aqua-200 !tw-text-aqua-700 !tw-px-2 !tw-py-1",
          };
        }

        groups.push(mentorGroup);
      }

      if (router.hasRoute(LESSON_CLASS_ROUTE_NAMES.MAIN)) {
        groups.push({
          key: "lesson",
          meta: {
            title: "Mijn lesgroepen",
            icon: toClassicIcon(usersIcon),
          },
          children: withPlaceholder(
            lessonClasses.value.map((item) => ({
              key: `lesson-${item.id}`,
              name: LESSON_CLASS_ROUTE_NAMES.MAIN,
              to: {
                name: LESSON_CLASS_ROUTE_NAMES.MAIN,
                params: { lessonClass: String(item.id) },
              },
              // Schools can reuse a lesson_class name across subjects, so always
              // show the subject shortage: "{lesson_class} - {subject_shortage}".
              meta: { title: `${item.name} - ${item.subjectShortage}` },
            })),
            "lesson",
            "Geen lesgroepen",
          ),
        });
      }

      return groups;
    });

    const loadClasses = async ({
      forceFetch = false,
    }: {
      forceFetch?: boolean;
    } = {}): Promise<void> => {
      if (isFetching.value.classes) {
        return isFetching.value.classes;
      }

      if (
        forceFetch === false &&
        shouldFetch(hasFetched.value.classes) === false
      ) {
        return;
      }

      isFetching.value.classes = (async () => {
        try {
          // On a fresh page load the auth user is fetched asynchronously, so
          // ensure it has resolved before firing (loadCurrentUser dedupes
          // with the call Header.vue already makes).
          const authStore = useAuthStore();
          await authStore.loadCurrentUser();

          if (authStore.user === null) {
            return;
          }

          const [mentor, lesson] = await Promise.all([
            fetchTeacherMentorClasses(),
            fetchTeacherLessonGroups(),
          ]);

          mentorClasses.value = mentor;
          lessonClasses.value = lesson;

          hasFetched.value.classes = new Date();
        } finally {
          isFetching.value.classes = false;
        }
      })();

      return isFetching.value.classes;
    };

    const $reset = (): void => {
      mentorClasses.value = [];
      lessonClasses.value = [];
      resetHasFetched(hasFetched.value);
    };

    return {
      mentorClasses,
      lessonClasses,
      isFetching,
      hasFetched,
      canSeeClassroomMenu,
      menuGroups,
      loadClasses,
      $reset,
    };
  },
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useClassroomNavigationStore, import.meta.hot),
  );
}
